import { useEffect, useState } from "react";
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs 
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { sendEmail } from "../components/sendemail";

export default function Dashboard() {
  const [totalRaised, setTotalRaised] = useState(800);
  const [tutor, setTutor] = useState(null);
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState(false);

  const [editForm, setEditForm] = useState({
    name: "",
    bio: "",
    schoolYear: "",
    subjects: [],
    gradeLevels: [],
    availability: {},
    maxStudents: 1
  });

  useEffect(() => {
    const fetchData = async () => {
      const settingsSnap = await getDoc(doc(db, "settings", "general"));
if (settingsSnap.exists()) {
  setTotalRaised(settingsSnap.data().totalRaised || 800);
}


      const tutorRef = doc(db, "users", auth.currentUser.uid);
      const tutorSnap = await getDoc(tutorRef);

      if (tutorSnap.exists()) {
        const data = tutorSnap.data();
        setTutor(data);

        setEditForm({
          name: data.name,
          bio: data.bio,
          schoolYear: data.schoolYear,
          subjects: data.subjects || [],
           gradeLevels: data.gradeLevels || [],
          availability: data.availability || {},
          maxStudents: data.maxStudents || 1
        });
      }

      const q = query(
        collection(db, "requests"),
        where("tutorId", "==", auth.currentUser.uid)
      );

      const snap = await getDocs(q);

      setRequests(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
    };

    fetchData();
  }, []);

  if (!tutor) return null;

  const toggleCapacity = async () => {
    const ref = doc(db, "users", auth.currentUser.uid);

    await updateDoc(ref, {
      atCapacity: !tutor.atCapacity
    });

    setTutor({ ...tutor, atCapacity: !tutor.atCapacity });
  };

const acceptRequest = async (requestId) => {
  const ref = doc(db, "requests", requestId);
  await updateDoc(ref, { accepted: true });

  const request = requests.find(r => r.id === requestId);

  const availabilityText = Object.entries(tutor.availability || {})
    .map(([day, times]) => `${day}: ${Array.isArray(times) ? times.join(", ") : times}`)
    .join(" | ");

  await sendEmail(
    request.email,
    "You've Been Matched with a SOWS Tutor!",
    `<p>Hi <strong>${request.name}</strong> and <strong>${tutor.name}</strong>,</p>
    <p>Welcome to SOWS Tutoring!</p>
    <p>We're excited to let you know that <strong>${tutor.name}</strong> has accepted your tutoring request through the SOWS Fund tutoring program! You've both been CC'd on this email so you can connect directly and begin coordinating your first session!</p>
    <p>Prior to beginning your first session, <strong>${tutor.name}</strong> will walk you through:</p>
    <ul>
      <li>How tutoring sessions will operate</li>
      <li>Helpful session preparation</li>
      <li>Expectations and goals</li>
      <li>Providing <a href="https://docs.google.com/forms/d/e/1FAIpQLSe9FQqN8ldbDy9sHy13Wd_R8di6pNTBSjJDbHHCK4Ydlz3JJg/viewform?usp=header">Feedback</a></li>
    </ul>
    <p>To receive future communications in regards to the tutoring program, please join the <a href="https://chat.whatsapp.com/Ltk41VBkPaT927t83Oc3bJ?mode=gi_t">Tutoring Announcement</a> chat.</p>
    <h3>How your Participation Helps Others!</h3>
    <p>Although this program is completely free to students, our tutors volunteer their time in service of the SOWS Fund, which raises money for Gazan students pursuing higher education around the world.</p>
    <p><strong>IMPACT:</strong> Thanks to the generosity of our students' families, we've already collected over <strong>$${totalRaised}</strong> in support of Gazan students. Your contribution, of any size, helps extend the impact of your child's learning to Gazan students pursuing their own.</p>
    <p style="text-align:center"><a href="https://leonardeducation.org/the-seeds-of-a-watermelon-scholarship-fund/"><strong>— CONTRIBUTE HERE —</strong></a></p>
    <p>Please feel free to share any questions, comments, or concerns with us at: <a href="mailto:sowstutoring@leonardeducation.org">sowstutoring@leonardeducation.org</a>.</p>
    <p>With Gratitude,<br/>SOWS</p>`,
    [tutor.email, "sowstutoring@leonardeducation.org", request.pemail].filter(Boolean)    // CC tutor, org, and parent
  );

  setRequests(prev =>
    prev.map(r => r.id === requestId ? { ...r, accepted: true } : r)
  );
};

  const toggleAvailability = (day, time) => {
    setEditForm(prev => {
      const dayTimes = prev.availability[day] || [];

      let updatedTimes;

      if (dayTimes.includes(time)) {
        updatedTimes = dayTimes.filter(t => t !== time);
      } else {
        updatedTimes = [...dayTimes, time];
      }

      return {
        ...prev,
        availability: {
          ...prev.availability,
          [day]: updatedTimes
        }
      };
    });
  };

  const saveProfile = async () => {
    const ref = doc(db, "users", auth.currentUser.uid);

    await updateDoc(ref, editForm);

    setTutor({ ...tutor, ...editForm });
    setEditing(false);
  };

  /* ✅ NEW: COMPLETE SESSION FUNCTION */
  const completeSession = async () => {
    const ref = doc(db, "users", auth.currentUser.uid);

    const newHours = (tutor.totalHours || 0) + 1;

    await updateDoc(ref, {
      totalHours: newHours
    });

    setTutor({ ...tutor, totalHours: newHours });
  };

  return (
    <div className="dashboard-container">

      <h1 className="dashboard-title">Tutor Dashboard</h1>

      {/* TOP ROW */}
      <div className="dashboard-top">

        {/* LEFT SIDE */}
        <div className="dashboard-left">

          <h2>Your Account</h2>

          <p
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              marginBottom: "25px",
              color: tutor.atCapacity ? "#e57386" : "#3d4f0c"
            }}
          >
            Status: {tutor.atCapacity ? "At Capacity" : "Accepting Students"}
          </p>

          {!editing ? (
            <>
              <p><strong>Name:</strong> {tutor.name}</p>
              <p><strong>Email:</strong> {tutor.email}</p>
              <p><strong>School Year:</strong> {tutor.schoolYear}</p>
              <p><strong>Subjects:</strong> {tutor.subjects.join(", ")}</p>
              <p>
                <strong>Current Students:</strong>{" "}
                {tutor.currentStudents} / {tutor.maxStudents}
              </p>

              <button
                className="capacity-btn"
                onClick={() => setEditing(true)}
              >
                Edit Profile
              </button>
            </>
          ) : (
            <div className="edit-form">

  <div className="form-group">
    <label>Name</label>
    <input
      value={editForm.name}
      onChange={e => setEditForm({...editForm, name: e.target.value})}
    />
  </div>

  <div className="form-group">
    <label>Bio</label>
    <textarea
      value={editForm.bio}
      onChange={e => setEditForm({...editForm, bio: e.target.value})}
    />
  </div>

  <div className="form-group">
    <label>School Year</label>
    <input
      value={editForm.schoolYear}
      onChange={e => setEditForm({...editForm, schoolYear: e.target.value})}
    />
  </div>

  <div className="form-group">
    <label>Max Students</label>
    <input
      type="number"
      min="1"
      value={editForm.maxStudents}
      onChange={e =>
        setEditForm({...editForm, maxStudents: Number(e.target.value)})
      }
    />
  </div>

  {/* ✅ SUBJECTS */}
  <div className="form-group">
    <label>Subjects You Can Tutor</label>
    <div className="subject-checkboxes">
      {["English", "Math", "Biology", "Chemistry", "Physics", "Standardized Tests", "Applications"].map(subject => (
        <label key={subject}>
          <input
            type="checkbox"
            checked={editForm.subjects.includes(subject)}
            onChange={() => {
              if (editForm.subjects.includes(subject)) {
                setEditForm({...editForm, subjects: editForm.subjects.filter(s => s !== subject)});
              } else {
                setEditForm({...editForm, subjects: [...editForm.subjects, subject]});
              }
            }}
          />
          {" "}{subject}
        </label>
      ))}
    </div>
  </div>
  {/* ✅ GRADE LEVELS */}
<div className="form-group">
  <label>Grade Levels You're Comfortable Tutoring</label>
  <div className="subject-checkboxes">
    {["Elementary School", "Middle School", "High School", "College"].map(level => (
      <label key={level}>
        <input
          type="checkbox"
          checked={(editForm.gradeLevels || []).includes(level)}
          onChange={() => {
            const current = editForm.gradeLevels || [];
            if (current.includes(level)) {
              setEditForm({...editForm, gradeLevels: current.filter(g => g !== level)});
            } else {
              setEditForm({...editForm, gradeLevels: [...current, level]});
            }
          }}
        />
        {" "}{level}
      </label>
    ))}
  </div>
</div>
  {/* ✅ AVAILABILITY - Toggle Chips */}
<div className="form-group">
  <label>Availability</label>
  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => (
    <div key={day} style={{ marginBottom: "12px" }}>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
        <span style={{
          padding: "8px 16px",
          borderRadius: "40px",
          fontWeight: "bold",
          fontSize: "15px",
          background: editForm.availability[day]?.length > 0 ? "#3d4f0c" : "#d4cebc",
          color: editForm.availability[day]?.length > 0 ? "#f6f0dc" : "#555",
          minWidth: "100px",
          textAlign: "center"
        }}>
          {day}
        </span>
        {["Morning", "Afternoon", "Evening"].map(time => {
          const selected = editForm.availability[day]?.includes(time);
          return (
            <span
              key={time}
              onClick={() => toggleAvailability(day, time)}
              style={{
                padding: "8px 16px",
                borderRadius: "40px",
                fontWeight: "600",
                fontSize: "14px",
                cursor: "pointer",
                background: selected ? "#e57386" : "#f6f0dc",
                color: selected ? "black" : "#555",
                border: selected ? "2px solid #e57386" : "2px solid #d4cebc",
                transition: "all 0.2s",
                userSelect: "none"
              }}
            >
              {selected ? "✓ " : ""}{time}
            </span>
          );
        })}
      </div>
    </div>
  ))}
</div>

  <button className="capacity-btn" onClick={saveProfile}>
    Save Changes
  </button>

</div>
          )}

          <button
            className="capacity-btn"
            style={{ marginTop: "20px" }}
            onClick={toggleCapacity}
          >
            {tutor.atCapacity ? "Mark Accepting Students" : "Mark At Capacity"}
          </button>

        </div>

        {/* RIGHT SIDE */}
        <div className="dashboard-right">

          <h2>Tutoring Requests</h2>

          {requests.length === 0 && <p>No requests yet</p>}

          {requests.map(r => (
            <div key={r.id} className="request-card">
              <p><strong>Name:</strong> {r.name}</p>
              <p><strong>Grade:</strong> {r.grade}</p>
              <p><strong>Subjects:</strong> {r.subjects}</p>
              <p><strong>Availability:</strong> {r.availability}</p>
              <p><strong>Notes:</strong> {r.notes}</p>

              {!r.accepted && (
                <button
                  className="accept-btn"
                  onClick={() => acceptRequest(r.id)}
                >
                  Accept Request
                </button>
              )}

              {r.accepted && (
                <p className="accepted-text">Accepted ✅</p>
              )}
            </div>
          ))}

        </div>
      </div>

      {/* HOURS + SESSION */}
      <div className="hours-section">

        {/* HOURS */}
        <div className="hours-card">
          <h2>Log Your Hours</h2>

          <p>Every 2 weeks, fill out the form below to track your service hours.</p>
          <p>This helps record your participation for in-kind donation reporting.</p>

          <a
            href="https://docs.google.com/forms/d/1v-A7fo8fL9r3qmBq-mEEVine8wAqbU1o5fO8Svwh4_4/viewform?edit_requested=true"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="capacity-btn">
              Submit Hours Form
            </button>
          </a>
        </div>

        {/* SESSION + HOURS DISPLAY */}
        <div className="session-card">

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

            <div>
              <h2>Completed a Session?</h2>

              <p>
                An automated email will be sent to the student reminding them to donate to SOWS.
              </p>

              <button className="capacity-btn" onClick={completeSession}>
                Completed a Session
              </button>
            </div>

            <div style={{ textAlign: "right" }}>
              <h3 style={{ color: "#d36c7a" }}>Total Hours Tutored</h3>
              <h1 style={{ fontSize: "60px", color: "#d36c7a", margin: 0 }}>
                {tutor.totalHours || 0}
              </h1>
            </div>

          </div>

        </div>

      </div>

    </div>
  );
}